import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useAuth, useToast } from "./";
import { RenewalForecastSummaryService } from "../services/RevenueForecastSummaryService";
import { NOTIFICATION_TYPES, getFormattedMoney } from "../utils";

const CACHE_KEY_PREFIX = "forecast_cache_v2";
const CACHE_DURATION = 360 * 60 * 1000; // 6 hours
const OWNER_SCOPE_STORAGE_KEY = "owner_scope";

// Get current ownerScope from localStorage
const getCurrentOwnerScope = () =>
  localStorage.getItem(OWNER_SCOPE_STORAGE_KEY) || "all";

// Generate cache key based on ownerScope
const getCacheKey = (ownerScope) =>
  `${CACHE_KEY_PREFIX}_${ownerScope || "all"}`;

export const useRenewalForecastSummary = () => {
  const { apiClient } = useAuth();
  const showToast = useToast();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const fetchingRef = useRef(new Set());
  const failedRef = useRef(new Set());
  const ownerScopeRef = useRef(getCurrentOwnerScope());

  // Load from localStorage on init - use ownerScope-specific cache
  const [cachedData, setCachedData] = useState(() => {
    try {
      const cacheKey = getCacheKey(ownerScopeRef.current);
      const stored = localStorage.getItem(cacheKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed;
      }
    } catch (error) {
      console.error("Error loading cache:", error);
    }
    return {};
  });

  // Wrap service in useMemo to prevent recreation on every render
  const service = useMemo(
    () => new RenewalForecastSummaryService(apiClient, showToast),
    [apiClient, showToast],
  );

  // Save to localStorage when cache changes - use ownerScope-specific key
  useEffect(() => {
    try {
      const cacheKey = getCacheKey(ownerScopeRef.current);
      localStorage.setItem(cacheKey, JSON.stringify(cachedData));
    } catch (error) {
      console.error("Error saving cache:", error);
    }
  }, [cachedData]);

  // Listen for ownerScope changes via custom event
  useEffect(() => {
    const handleOwnerScopeChange = (event) => {
      const newOwnerScope = event.detail;
      if (ownerScopeRef.current !== newOwnerScope) {
        ownerScopeRef.current = newOwnerScope;
        // Load cache for new ownerScope
        try {
          const cacheKey = getCacheKey(newOwnerScope);
          const stored = localStorage.getItem(cacheKey);
          if (stored) {
            setCachedData(JSON.parse(stored));
          } else {
            setCachedData({});
          }
        } catch (error) {
          console.error("Error loading cache for new ownerScope:", error);
          setCachedData({});
        }
        setErrors({});
        fetchingRef.current.clear();
        failedRef.current.clear();
      }
    };

    window.addEventListener("ownerScopeChanged", handleOwnerScopeChange);
    return () => {
      window.removeEventListener("ownerScopeChanged", handleOwnerScopeChange);
    };
  }, []);

  const isCacheValid = useCallback(
    (timeRange) => {
      const cached = cachedData[timeRange];
      if (!cached?.timestamp) return false;

      const age = Date.now() - cached.timestamp;
      return age < CACHE_DURATION;
    },
    [cachedData],
  );

  const fetchMultipleTimeRanges = useCallback(
    async (timeRanges) => {
      const uncachedRanges = timeRanges.filter(
        (range) =>
          !isCacheValid(range) &&
          !fetchingRef.current.has(range) &&
          !failedRef.current.has(range),
      );

      if (uncachedRanges.length === 0) {
        return;
      }

      uncachedRanges.forEach((range) => fetchingRef.current.add(range));
      setLoading(true);

      try {
        const results = await Promise.all(
          uncachedRanges.map(async (timeRange) => {
            try {
              const params =
                RenewalForecastSummaryService.mapTimeRangeToParams(timeRange);
              const data = await service.getRenewalForecastSummary(params);

              // Clear error on success
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[timeRange];
                return newErrors;
              });

              return {
                timeRange,
                data: {
                  ...data,
                  timestamp: Date.now(),
                },
              };
            } catch (error) {
              console.error(`Error fetching ${timeRange}:`, error);

              // Prevent automatic retry on failure
              failedRef.current.add(timeRange);

              // Store error
              setErrors((prev) => ({
                ...prev,
                [timeRange]: {
                  message: error.message || "Failed to fetch data",
                  timestamp: Date.now(),
                  status: error.response?.status,
                },
              }));

              if (showToast) {
                showToast({
                  content: `Failed to fetch ${timeRange}`,
                  type: NOTIFICATION_TYPES.error,
                });
              }

              return { timeRange, data: null };
            }
          }),
        );

        setCachedData((prev) => {
          const newCache = { ...prev };
          results.forEach(({ timeRange, data }) => {
            if (data) {
              newCache[timeRange] = data;
            }
          });
          return newCache;
        });
      } finally {
        uncachedRanges.forEach((range) => fetchingRef.current.delete(range));
        setLoading(false);
      }
    },
    [service, isCacheValid, showToast],
  );

  const getTimeRangeData = useCallback(
    (timeRange) => {
      const cached = cachedData[timeRange];
      if (!cached) {
        return null;
      }

      if (!isCacheValid(timeRange)) {
        return null;
      }

      // Check if data already has timestamp (from cache)
      // If so, return without timestamp. Otherwise return as-is
      if (cached.timestamp) {
        const { timestamp, ...data } = cached;
        return data;
      }

      return cached;
    },
    [cachedData, isCacheValid],
  );

  const getTimeRangeError = useCallback(
    (timeRange) => {
      return errors[timeRange] || null;
    },
    [errors],
  );

  const clearCache = useCallback(() => {
    setCachedData({});
    setErrors({});
    failedRef.current.clear();
    try {
      const cacheKey = getCacheKey(ownerScopeRef.current);
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error("Error clearing cache:", error);
    }
  }, []);

  // Transform data for renewal forecast (keep if you were using these)
  const transformDataForRenewalForecast = useCallback((data) => {
    if (!data) return { openOpportunities: [], closedOpportunities: [] };

    const openOpportunities = [];
    const closedOpportunities = [];

    if (data.open_opportunities_per_type?.summary) {
      Object.entries(data.open_opportunities_per_type.summary).forEach(
        ([oppType, values]) => {
          if (oppType !== "Total" && oppType !== "Total (all open opps)") {
            openOpportunities.push({
              oppType,
              accounts: values.accounts || 0,
              contracts: values.contracts || 0,
              deltaMrr: getFormattedMoney(values.delta_mrr),
              deltaArr: getFormattedMoney(values.delta_arr),
            });
          }
        },
      );
    }

    if (data.closed_opportunities_per_type?.summary) {
      Object.entries(data.closed_opportunities_per_type.summary).forEach(
        ([oppType, values]) => {
          if (oppType !== "Total" && oppType !== "Total (all closed opps)") {
            closedOpportunities.push({
              oppType,
              accounts: values.accounts || 0,
              contracts: values.contracts || 0,
              deltaMrr: getFormattedMoney(values.delta_mrr),
              deltaArr: getFormattedMoney(values.delta_arr),
            });
          }
        },
      );
    }

    return { openOpportunities, closedOpportunities };
  }, []);

  const transformDataForDetailedView = useCallback((data, isOpen = true) => {
    if (!data) return [];

    const accountData = isOpen
      ? data.open_opportunities_per_account
      : data.closed_opportunities_per_account;

    if (!accountData?.deals) return [];

    return accountData.deals.map((deal) => {
      return {
        contractName: deal.contract_name || "",
        accountName: deal.account_name || "",
        accountId: deal.account_id || "",
        logo: deal.account_logo || "",
        csmName: deal.csm_name || "",
        oppType: deal.opp_type || "",
        oppName: deal.opp_name || "",
        stage: deal.stage || "",
        closeDate: deal.close_date || "",
        deltaMrr: getFormattedMoney(deal.delta_mrr),
        deltaArr: getFormattedMoney(deal.delta_arr),
        pctChange: deal.pct_change || "0%",
        logoLost: deal.logo_lost || "No",
      };
    });
  }, []);

  const getTotalsForDetailedView = useCallback((data, isOpen = true) => {
    if (!data)
      return {
        deltaMrr: getFormattedMoney(null),
        deltaArr: getFormattedMoney(null),
      };

    const accountData = isOpen
      ? data.open_opportunities_per_account
      : data.closed_opportunities_per_account;

    if (!accountData?.totals)
      return {
        deltaMrr: getFormattedMoney(null),
        deltaArr: getFormattedMoney(null),
      };

    return {
      deltaMrr: getFormattedMoney(accountData.totals.delta_mrr),
      deltaArr: getFormattedMoney(accountData.totals.delta_arr),
    };
  }, []);

  const getGrandTotalsFromPerType = useCallback((data, isOpen = true) => {
    const defaultReturn = {
      accounts: 0,
      contracts: 0,
      deltaMrr: getFormattedMoney(null),
      deltaArr: getFormattedMoney(null),
    };

    if (!data) return defaultReturn;

    const typeData = isOpen
      ? data.open_opportunities_per_type
      : data.closed_opportunities_per_type;

    if (!typeData?.summary) return defaultReturn;

    const totalKey = Object.keys(typeData.summary).find((key) =>
      key.toLowerCase().includes("total"),
    );

    if (!totalKey || !typeData.summary[totalKey]) return defaultReturn;

    const totals = typeData.summary[totalKey];

    return {
      accounts: totals.accounts === "—" ? 0 : totals.accounts || 0,
      contracts: totals.contracts || 0,
      deltaMrr: getFormattedMoney(totals.delta_mrr),
      deltaArr: getFormattedMoney(totals.delta_arr),
    };
  }, []);

  const transformForecastMetrics = useCallback((data) => {
    if (!data?.revenue_forecast_summary?.revenue_forecast) return null;

    const rf = data.revenue_forecast_summary.revenue_forecast;

    return {
      startMrr: rf.start_mrr?.value || 0,
      netDeltaMrr: rf.net_delta_mrr?.value || 0,
      newMrr: rf.new_mrr?.value || 0,
      forecastEndMrr: rf.forecast_end_mrr?.value || 0,
      forecastArr: rf.forecast_arr?.value || 0,
      forecastDeltaArr: rf.forecast_delta_arr?.value || 0,
      // PAN-461: `?? null`, NOT `|| 0`.
      //
      // The backend now sends null for these when the viewer's own accounts
      // have no renewals due — there is no retention figure to report, and the
      // previous 100% was a fabrication. `|| 0` would turn that null into 0%,
      // which is false in the other direction and reads as a catastrophic
      // quarter rather than a perfect one.
      //
      // Measured in production: 33 of 44 CSMs on the teams where owner scope is
      // meaningful have zero renewals due this quarter.
      grossRevenueRetention: rf.gross_revenue_retention?.raw_value ?? null,
      netRevenueRetention: rf.net_revenue_retention?.raw_value ?? null,
      logoRetention: {
        percentage: rf.logo_retention?.raw_value ?? null,
        description: rf.logo_retention?.tooltip || "",
      },
      coverage: {
        percentage: rf.coverage?.raw_value ?? null,
        description: rf.coverage?.tooltip || "",
      },
    };
  }, []);

  const transformRevenueDrivers = (data) => {
    const drivers = data?.revenue_forecast_summary?.revenue_drivers;

    if (!drivers) {
      return null;
    }

    return {
      renewalBase: {
        closed: drivers.renewal_base?.closed_to_date,
        remaining: drivers.renewal_base?.remaining_expected,
        total: drivers.renewal_base?.total_expected,
      },
      expansion: {
        closed: drivers.expansion?.closed_to_date,
        remaining: drivers.expansion?.remaining_expected,
        total: drivers.expansion?.total_expected,
      },
      contraction: {
        closed: drivers.contraction?.closed_to_date,
        remaining: drivers.contraction?.remaining_expected,
        total: drivers.contraction?.total_expected,
      },
      churn: {
        closed: drivers.churn?.closed_to_date,
        remaining: drivers.churn?.remaining_expected,
        total: drivers.churn?.total_expected,
      },
      newBusiness: {
        closed: drivers.new_mrr?.closed_to_date,
        remaining: drivers.new_mrr?.remaining_expected,
        total: drivers.new_mrr?.total_expected,
      },
      allClosed:
        drivers.renewal_base.closed_to_date_raw +
        drivers.expansion.closed_to_date_raw +
        drivers.contraction.closed_to_date_raw +
        drivers.churn.closed_to_date_raw,
      allRemaining:
        drivers.renewal_base.remaining_expected_raw +
        drivers.expansion.remaining_expected_raw +
        drivers.contraction.remaining_expected_raw +
        drivers.churn.remaining_expected_raw,
      allTotal:
        drivers.renewal_base.total_expected_raw +
        drivers.expansion.total_expected_raw +
        drivers.contraction.total_expected_raw +
        drivers.churn.total_expected_raw,
    };
  };

  return {
    loading,
    cachedData,
    errors,
    fetchMultipleTimeRanges,
    getTimeRangeData,
    getTimeRangeError,
    clearCache,
    transformDataForRenewalForecast,
    transformDataForDetailedView,
    getTotalsForDetailedView,
    getGrandTotalsFromPerType,
    transformForecastMetrics,
    transformRevenueDrivers,
  };
};
